
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { createGroupUrl } from '../../../hooks/useNavigation';

interface Group {
  id: string;
  sequential_id: number;
  name: string;
  description: string;
  cover_image: string;
  category: string;
  privacy: string;
  member_count: number;
}

interface GroupMembership {
  group_id: string;
  role: string;
  joined_at: string;
  groups: Group;
}

interface MyGroupsListProps {
  userId: string;
}

const MyGroupsList = ({ userId }: MyGroupsListProps) => {
  const [memberships, setMemberships] = useState<GroupMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'owner' | 'admin' | 'member'>('all');

  useEffect(() => {
    loadMyGroups();
  }, [userId]);

  const loadMyGroups = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('group_members')
        .select(`
          group_id,
          role,
          joined_at,
          groups (
            id,
            sequential_id,
            name,
            description,
            cover_image,
            category,
            privacy,
            member_count
          )
        `)
        .eq('user_id', userId)
        .order('joined_at', { ascending: false });

      if (error) {
        console.error('Error loading groups:', error);
        setMemberships([]);
      } else {
        setMemberships((data as any) || []);
      }
    } catch (err) {
      console.error('Error:', err);
      setMemberships([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveGroup = async (groupId: string, memberCount: number) => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;

    try {
      await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('user_id', userId);

      await supabase
        .from('groups')
        .update({ member_count: memberCount - 1 })
        .eq('id', groupId);

      loadMyGroups();
    } catch (error) {
      console.error('Error leaving group:', error);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner':
        return (
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
            Owner
          </span>
        );
      case 'admin':
        return (
          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
            Admin
          </span>
        );
      case 'moderator':
        return (
          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
            Moderator
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
            Member
          </span>
        );
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Entertainment': 'bg-purple-100 text-purple-700',
      'Production': 'bg-blue-100 text-blue-700',
      'Genre': 'bg-pink-100 text-pink-700',
      'Regional': 'bg-green-100 text-green-700',
      'Professional': 'bg-orange-100 text-orange-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const filteredMemberships = memberships.filter((m) => {
    if (filter === 'all') return true;
    if (filter === 'owner') return m.role === 'owner';
    if (filter === 'admin') return m.role === 'admin' || m.role === 'moderator';
    return m.role === 'member';
  });

  const stats = {
    total: memberships.length,
    owned: memberships.filter((m) => m.role === 'owner').length,
    admin: memberships.filter((m) => m.role === 'admin' || m.role === 'moderator').length,
    member: memberships.filter((m) => m.role === 'member').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Loading your groups...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-3">
        <button
          onClick={() => setFilter('all')}
          className={`p-3 rounded-xl text-center transition-all cursor-pointer ${
            filter === 'all' ? 'bg-teal-50 border-2 border-teal-500' : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
          }`}
        >
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-xs text-gray-500">All Groups</p>
        </button>
        <button
          onClick={() => setFilter('owner')}
          className={`p-3 rounded-xl text-center transition-all cursor-pointer ${
            filter === 'owner' ? 'bg-amber-50 border-2 border-amber-500' : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
          }`}
        >
          <p className="text-2xl font-bold text-amber-600">{stats.owned}</p>
          <p className="text-xs text-gray-500">Owned</p>
        </button>
        <button
          onClick={() => setFilter('admin')}
          className={`p-3 rounded-xl text-center transition-all cursor-pointer ${
            filter === 'admin' ? 'bg-purple-50 border-2 border-purple-500' : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
          }`}
        >
          <p className="text-2xl font-bold text-purple-600">{stats.admin}</p>
          <p className="text-xs text-gray-500">Admin</p>
        </button>
        <button
          onClick={() => setFilter('member')}
          className={`p-3 rounded-xl text-center transition-all cursor-pointer ${
            filter === 'member' ? 'bg-gray-100 border-2 border-gray-400' : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
          }`}
        >
          <p className="text-2xl font-bold text-gray-600">{stats.member}</p>
          <p className="text-xs text-gray-500">Member</p>
        </button>
      </div>

      {/* Groups List */}
      {filteredMemberships.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-group-line text-3xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {filter === 'all' ? 'No groups yet' : `No ${filter} groups`}
          </h3>
          <p className="text-gray-600 mb-4">
            {filter === 'all'
              ? 'Join groups to connect with fellow enthusiasts'
              : 'You don\'t have any groups with this role'}
          </p>
          <Link
            to="/groups"
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer"
          >
            <i className="ri-compass-3-line"></i>
            Explore Groups
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredMemberships.map((membership) => {
            const group = membership.groups;
            if (!group) return null;
            
            const groupUrl = createGroupUrl(group.sequential_id, group.name);
            
            return (
              <div
                key={membership.group_id}
                className="bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-4">
                  <Link to={groupUrl} className="flex-shrink-0">
                    <img
                      src={group.cover_image || 'https://public.readdy.ai/ai/img_res/5bcbba568e83d000c2956dc8a4e54ee2.jpg'}
                      alt={group.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Link
                        to={groupUrl}
                        className="font-semibold text-gray-900 hover:text-teal-600 transition-colors truncate"
                      >
                        {group.name}
                      </Link>
                      {group.privacy === 'private' && (
                        <i className="ri-lock-line text-gray-400 text-sm"></i>
                      )}
                      {getRoleBadge(membership.role)}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-1 mb-2">{group.description}</p>
                    <div className="flex items-center gap-3 text-xs">
                      <span className={`px-2 py-0.5 rounded-full ${getCategoryColor(group.category)}`}>
                        {group.category}
                      </span>
                      <span className="text-gray-500 flex items-center gap-1">
                        <i className="ri-group-line"></i>
                        {group.member_count} members
                      </span>
                      <span className="text-gray-400">
                        Joined {new Date(membership.joined_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={groupUrl}
                      className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                      title="View Group"
                    >
                      <i className="ri-arrow-right-line text-lg"></i>
                    </Link>
                    {membership.role !== 'owner' && (
                      <button
                        onClick={() => handleLeaveGroup(group.id, group.member_count)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        title="Leave Group"
                      >
                        <i className="ri-logout-box-r-line text-lg"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Explore More Link */}
      {memberships.length > 0 && (
        <div className="text-center pt-4">
          <Link
            to="/groups"
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
          >
            <i className="ri-compass-3-line"></i>
            Discover More Groups
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      )}
    </div>
  );
};

export default MyGroupsList;
